import clsx from "clsx"
import { useState } from "react"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { Bullet, ExtraTouchArea, Skeleton, TruncatableText } from "@particles/index"

import { Icon } from "@atoms/index"

import styles from "./GroupLabel.module.scss"
import type { GroupLabelProps } from "./GroupLabel.types"

export default function GroupLabel(props: GroupLabelProps) {
    const {
        variant,
        prefixSlot = null,
        label,
        middleSlot = null,
        counter = "",
        caption = "",
        suffixSlot = null,

        isCollapsible = false,
        defaultCollapsed = true,
        onCollapse,

        showPrefixSlot = true,
        showMiddleSlot = true,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isLoading = false,
    } = props

    const [isLabelTruncated, setIsLabelTruncated] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

    const isInteractive = isCollapsible && !isDisabled && !isLoading

    // Primary variant: left slot, counter, caption, middleSlot; caret on LEFT
    // Secondary variant: simplified — no counter, no caption, no middleSlot; caret on RIGHT
    const isPrimary = variant === "primary"
    const isSecondary = variant === "secondary"

    const allowPrefixSlot = showPrefixSlot && isPrimary
    const allowMiddleSlot = showMiddleSlot && isPrimary
    const shouldDisplayPrefixSlot = allowPrefixSlot && Boolean(prefixSlot)
    const shouldDisplayMiddleSlot = allowMiddleSlot && Boolean(middleSlot)
    const shouldDisplayCounter = Boolean(counter) && isPrimary
    const shouldDisplayCaption = !isLabelTruncated && Boolean(caption) && isPrimary
    // Bullet between the label and the first meta item (middleSlot / counter / caption)
    const shouldDisplayBullet1 = isPrimary && (shouldDisplayMiddleSlot || shouldDisplayCounter || shouldDisplayCaption)
    // Bullet between counter and caption
    const shouldDisplayBullet2 = shouldDisplayCaption && (shouldDisplayCounter || shouldDisplayMiddleSlot)

    function toggleCollapse() {
        setIsCollapsed((previous) => {
            const next = !previous
            onCollapse?.(next)
            return next
        })
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        ifSpaceOrEnter(event, () => {
            toggleCollapse()
        })
    }

    const rootClassName = clsx(styles.base, styles[`${variant}Variant`], {
        [styles.isInteractive]: isInteractive,
        [styles.isDisabled]: isDisabled,
        [styles.isLoading]: isLoading,
        [styles.isFocused]: _isFocused,
        [styles.isHovered]: _isHovered,
        [styles.isPressed]: isPressed,
        [styles.isCollapsed]: isCollapsible && isCollapsed,
        [styles.isExpanded]: isCollapsible && !isCollapsed,
    })

    const chevronClassName = clsx(styles.chevron, {
        [styles.chevronCollapsed]: isCollapsed,
        [styles.chevronExpanded]: !isCollapsed,
    })

    return (
        <div
            className={rootClassName}
            role={isCollapsible ? "button" : undefined}
            tabIndex={isCollapsible ? (isInteractive ? 0 : -1) : undefined}
            aria-disabled={isCollapsible ? !isInteractive : undefined}
            aria-expanded={isCollapsible ? !isCollapsed : undefined}
            data-collapsed={isCollapsible ? isCollapsed : undefined}
            onClick={isInteractive ? toggleCollapse : undefined}
            onKeyDown={isInteractive ? handleKeyDown : undefined}
        >
            {isLoading ? (
                <div className={styles.labelContainer}>
                    <Skeleton width={96} height={10} borderRadius={0} />
                </div>
            ) : (
                <div className={styles.labelContainer}>
                    {/* Primary renders the caret on the LEFT, before containerPaddings */}
                    {isCollapsible && isPrimary && (
                        <Icon
                            icon={faIcon({ name: "caret-right", style: "solid" })}
                            className={chevronClassName}
                            size={12}
                            container="square"
                        />
                    )}
                    <div className={styles.containerPaddings}>
                        {shouldDisplayPrefixSlot ? prefixSlot : null}
                        <TruncatableText
                            text={label}
                            className={styles.label}
                            tooltipOnTruncate={true}
                            onTruncationChange={setIsLabelTruncated}
                        />
                        {/* Secondary renders the caret on the RIGHT, after the label */}
                        {isCollapsible && isSecondary && (
                            <Icon
                                icon={faIcon({ name: "caret-right", style: "solid" })}
                                className={chevronClassName}
                                size={12}
                                container="square"
                            />
                        )}
                        {shouldDisplayBullet1 ? <Bullet className={styles.bullet1} /> : null}
                        {shouldDisplayMiddleSlot ? middleSlot : null}
                        {shouldDisplayCounter ? <span className={styles.counter}>{counter}</span> : null}
                        {shouldDisplayBullet2 ? <Bullet className={styles.bullet2} /> : null}
                        {shouldDisplayCaption ? (
                            <TruncatableText text={caption} className={styles.caption} tooltipOnTruncate={true} />
                        ) : null}
                    </div>
                    <div className={styles.cta}>
                        <ExtraTouchArea extraTouchArea={2}>{suffixSlot}</ExtraTouchArea>
                    </div>
                </div>
            )}
        </div>
    )
}
